import React, {useCallback, useState} from "react";
import {ListItemIcon, Menu, MenuItem} from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import {
    HighlightOff, Redo, Undo,
    FormatIndentDecrease, FormatIndentIncrease,
    ArrowUpward, ArrowDownward, Edit
} from '@material-ui/icons';

const useStyles = makeStyles(() => ({
    contextPaper: {
        paddingTop: 0,
        paddingBottom: 0
    },
    menuItemRoot: {
        color: '#676767',
        '&:hover': {
            color: '#000000'
        }
    },
    listIconRoot: {
        minWidth: 30
    }
}));

const initialContextState = {
    mouseX: null,
    mouseY: null,
    actionPath: null,
    actionIndex: null,
    currentItemPath: null
};

const initialDisabled = {
    after: false, before: false, remove: false
};

export default function useContextMenu({addBefore, addAfter, remove, changeIndent, changePosition, editRecord}) {
    const [currentContext, setCurrentContext] = useState(initialContextState);
    const [disabled, setDisabled] = useState(initialDisabled);
    const classes = useStyles();

    const onOpen = (e, currentItemPath, actionPath, actionIndex, disabled) => {
        e.preventDefault();
        setCurrentContext({
            mouseX: e.clientX - 2,
            mouseY: e.clientY - 4,
            actionPath,
            actionIndex,
            currentItemPath
        });
        setDisabled(prev => {
            return {
                ...prev, ...disabled
            }
        })
    };
    const onClose = () => {
        setCurrentContext(initialContextState)
    };
    const ContextMenu = useCallback(() => (
        <Menu
            open={currentContext.mouseY !== null}
            classes={{list: classes.contextPaper}}
            anchorReference={"anchorPosition"}
            anchorPosition={
                currentContext.mouseY !== null && currentContext.mouseX !== null
                    ? {top: currentContext.mouseY, left: currentContext.mouseX}
                    : undefined
            }
            onClose={onClose}
        >
            {/* edit (only is from entityMixed) */}
            { editRecord && (
                <MenuItem
                    dense
                    classes={{root: classes.menuItemRoot}}
                    disabled={disabled.before}
                    onClick={(e) => {
                        e.stopPropagation();
                        editRecord(currentContext);
                        onClose();
                    }}
                >
                    <ListItemIcon classes={{root: classes.listIconRoot}}>
                        <Edit fontSize="small"/>
                    </ListItemIcon>
                    Edit
                </MenuItem>
            )}

            {/*add before/after*/}
            <MenuItem
                dense
                classes={{root: classes.menuItemRoot}}
                disabled={disabled.before}
                onClick={(e) => {
                    e.stopPropagation();
                    addBefore(currentContext.actionPath, currentContext.actionIndex);
                    onClose();
                }}
            >
                <ListItemIcon classes={{root: classes.listIconRoot}}>
                    <Redo fontSize="small"/>
                </ListItemIcon>
                Add Before
            </MenuItem>
            <MenuItem
                dense
                classes={{root: classes.menuItemRoot}}
                disabled={disabled.after}
                onClick={(e) => {
                    e.stopPropagation();
                    addAfter(currentContext.actionPath, currentContext.actionIndex + 1);
                    onClose();
                }}
            >
                <ListItemIcon classes={{root: classes.listIconRoot}}>
                    <Undo fontSize="small" style={{transform: 'rotate(180deg)'}}/>
                </ListItemIcon>
                Add After
            </MenuItem>

            {/*onChangeIndent*/}
            {changeIndent && (
                <>
                    <MenuItem
                        dense
                        classes={{root: classes.menuItemRoot}}
                        disabled={disabled.after}
                        onClick={(e) => {
                            e.stopPropagation();
                            changeIndent(
                                currentContext.currentItemPath,
                                'up',
                                currentContext.actionIndex
                            );
                            onClose();
                        }}
                    >
                        <ListItemIcon classes={{root: classes.listIconRoot}}>
                            <FormatIndentIncrease fontSize="small"/>
                        </ListItemIcon>
                        Increase Indent
                    </MenuItem>
                    <MenuItem
                        dense
                        classes={{root: classes.menuItemRoot}}
                        disabled={disabled.before}
                        onClick={(e) => {
                            e.stopPropagation();
                            changeIndent(
                                currentContext.currentItemPath,
                                'down',
                                currentContext.actionIndex
                            );
                            onClose();
                        }}
                    >
                        <ListItemIcon classes={{root: classes.listIconRoot}}>
                            <FormatIndentDecrease fontSize="small"/>
                        </ListItemIcon>
                        Decrease Indent
                    </MenuItem>
                </>
            )}

            {/*onChangePosition*/}
            {changePosition && (
                <>
                    <MenuItem
                        dense
                        classes={{root: classes.menuItemRoot}}
                        disabled={disabled.before}
                        onClick={(e) => {
                            e.stopPropagation();
                            changePosition(currentContext.currentItemPath, currentContext.actionPath, currentContext.actionIndex, currentContext.actionIndex - 1);
                            onClose();
                        }}
                    >
                        <ListItemIcon classes={{root: classes.listIconRoot}}>
                            <ArrowUpward fontSize="small"/>
                        </ListItemIcon>
                        Move Up
                    </MenuItem>
                    <MenuItem
                        dense
                        classes={{root: classes.menuItemRoot}}
                        disabled={disabled.after}
                        onClick={(e) => {
                            e.stopPropagation();
                            changePosition(currentContext.currentItemPath, currentContext.actionPath, currentContext.actionIndex, currentContext.actionIndex + 1);
                            onClose();
                        }}
                    >
                        <ListItemIcon classes={{root: classes.listIconRoot}}>
                            <ArrowDownward fontSize="small"/>
                        </ListItemIcon>
                        Move Down
                    </MenuItem>
                </>
            )}

            {/*remove*/}
            <MenuItem
                dense
                classes={{root: classes.menuItemRoot}}
                disabled={disabled.remove}
                onClick={(e) => {
                    e.stopPropagation();
                    remove(currentContext.currentItemPath);
                    onClose();
                }}
            >
                <ListItemIcon classes={{root: classes.listIconRoot}}>
                    <HighlightOff fontSize="small"/>
                </ListItemIcon>
                Remove
            </MenuItem>
        </Menu>
    ), [currentContext.mouseY]);

    return {
        ContextMenu,
        onOpen,
        isOpen: currentContext.mouseY !== null
    }

}